/**
 * Prompt Builder
 *
 * Builds professional photography/design prompts for Solomon image generation.
 * Uses industry-standard terminology to produce high-quality AI-generated images.
 */

/**
 * Context for image generation including type, dimensions, and existing image info
 */
export interface ImageGenerationContext {
  imageKey: string;
  aspectRatio: string;
  fieldName: string;
  displayName: string;
  existingImageUrl?: string;
  availableImages?: {
    vertical?: string;
    square?: string;
    horizontal?: string;
    circle?: string;
  };
}

/**
 * Options for building an image generation prompt
 */
export interface PromptBuilderOptions {
  artistName: string;
  context: ImageGenerationContext;
  hasReferenceImage?: boolean;
}

/**
 * Finds the best available reference image to use for generation.
 * Prioritizes square images as they're most common, then other formats.
 *
 * @param targetKey - The image type being generated (to exclude from options)
 * @param availableImages - Map of available image URLs by type
 * @returns The best reference image info, or null if none available
 */
function findBestReferenceImage(
  targetKey: string,
  availableImages?: ImageGenerationContext['availableImages']
): { name: string; url: string } | null {
  if (!availableImages) return null;

  // Priority order based on target (prefer square as it's most common)
  const priorities: Record<string, string[]> = {
    vertical: ['square', 'horizontal', 'circle'],
    square: ['vertical', 'horizontal', 'circle'],
    horizontal: ['square', 'vertical', 'circle'],
    circle: ['square', 'vertical', 'horizontal'],
  };

  const order = priorities[targetKey] || ['square', 'vertical', 'horizontal', 'circle'];

  // eslint-disable-next-line no-restricted-syntax
  for (const key of order) {
    const url = availableImages[key as keyof typeof availableImages];
    if (url) {
      const displayNames: Record<string, string> = {
        vertical: 'vertical profile image',
        square: 'square profile image',
        horizontal: 'horizontal profile image',
        circle: 'circle profile image',
      };
      return {
        name: displayNames[key],
        url,
      };
    }
  }
  return null;
}

/**
 * Builds a simple, action-oriented prompt for image generation.
 *
 * If an existing reference image is available, the prompt includes the URL
 * so Solomon can use generate_image_from_reference tool. Otherwise, a generic
 * prompt is returned for fresh image generation.
 *
 * @param options - The options including artist name and context
 * @returns A concise prompt string ready for submission
 */
export function buildImageGenerationPrompt(options: PromptBuilderOptions): string {
  const { context } = options;

  const referenceImage = findBestReferenceImage(context.imageKey, context.availableImages);

  if (referenceImage) {
    // Include the URL so Solomon can use generate_image_from_reference tool
    // to analyze the style and generate a new image in the target aspect ratio
    return `Generate a new ${context.aspectRatio} ${context.displayName} based on my existing ${referenceImage.name}. Use this image as reference: ${referenceImage.url}`;
  }

  return `Create a ${context.aspectRatio} ${context.displayName} for my ministry profile.`;
}
